import { useEffect, useState } from "react";
import dataService from "../../../helpers/dataService";
import useStore from "../../../context/useStore";
import { toast } from "react-toastify";
import { atom, useAtom } from "jotai";

const retryingJobsAtom = atom(new Set());
const retriedJobsAtom = atom(new Set());

let strTimeout = null;

const useOrientationJobList = (autoSearch = true) => {
  const [jobs, setJobs] = useState([]);
  const [state] = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState({
    filters: {
      terms: "",
      queue: "",
      dateDebut: "",
      dateFin: "",
    },
    page: 1,
    nbItemsByPage: 25,
  });
  const [nbPages, setNbPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [retriedJobs, setRetriedJobs] = useAtom(retriedJobsAtom);
  const [retryingJobs, setRetryingJobs] = useAtom(retryingJobsAtom);
  const amISuperAdmin = state.auth.user.role === "SUPER_ADMIN";

  const handleSearchChange = (e) => {
    setSearch({
      ...search,
      filters: { ...search.filters, [e.target.name]: e.target.value },
    });
  };

  const changePage = (newPage) => {
    setSearch({ ...search, page: newPage });
  };

  const changeNbItemsByPage = (newNbItems) => {
    setSearch({ ...search, nbItemsByPage: newNbItems, page: 1 });
  };

  const retryJob = (jobId) => {
    setRetryingJobs((prev) => new Set([...prev, jobId]));

    dataService.get(
      `jobs/failed/${jobId}/retry`,
      () => {
        toast.success("Job relancé avec succès");
        setRetriedJobs((prev) => new Set([...prev, jobId]));
      },
      (error) => {
        console.error("Erreur lors de la relance du job:", error);
        toast.error("Erreur lors de la relance du job");
      },
      () => {
        setRetryingJobs((prev) => {
          const newSet = new Set(prev);
          newSet.delete(jobId);
          return newSet;
        });
      }
    );
  };

  const clearFilters = () => {
    setSearch({
      filters: {
        terms: "",
        queue: "",
        dateDebut: "",
        dateFin: "",
      },
      page: 1,
      nbItemsByPage: 25,
    });
  };

  const fetchJobs = () => {
    setIsLoading(true);
    setErrors({});

    // Appel à l'API réelle
    dataService.post(
      "jobs/failed/search",
      {
        filters: {
          terms: search.filters.terms,
          queue: search.filters.queue,
          dateDebut: search.filters.dateDebut,
          dateFin: search.filters.dateFin,
        },
        page: search.page,
      },
      (response) => {
        const jobsWithTruncatedRedisId = response.data.map(job => ({
          ...job,
          redisId: job.redisId ? job.redisId.substring(0, 10) : job.redisId
        }));
        setJobs(jobsWithTruncatedRedisId);
        setTotalCount(response.count);
        setNbPages(Math.ceil(response.count / search.nbItemsByPage));
        setIsLoading(false);
      },
      (error) => {
        console.error("Erreur lors de la récupération des jobs:", error);
        setErrors({ fetch: "Erreur lors de la récupération des données" });
        setIsLoading(false);
        toast.error("Erreur lors de la récupération des jobs");
      }
    );
  };
  useEffect(() => {
    if (strTimeout) clearTimeout(strTimeout);
    if (!autoSearch) return;
    strTimeout = setTimeout(() => {
      fetchJobs();
    }, 500);
    return () => {
      if (strTimeout) clearTimeout(strTimeout);
    };
  }, [search]);

  const deleteJob = (jobId) => {
    setIsLoading(true);
    setErrors({});

    // Appel à l'API réelle
    dataService.remove(
      `jobs/failed/${jobId}`,
      {},
      () => {
        toast.success("Job supprimé");
        fetchJobs();
      },
      (error) => {
        toast.error("Erreur lors de la suppression du job");
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  return {
    jobs,
    isLoading,
    errors,
    nbPages,
    totalCount,
    search,
    changePage,
    changeNbItemsByPage,
    retryJob,
    retriedJobs,
    retryingJobs,
    fetchJobs,
    handleSearchChange,
    clearFilters,
    deleteJob,
    amISuperAdmin,
  };
};

export default useOrientationJobList;
